import React, { useState } from 'react';

import { Checkbox, Form, Input, Tag, Tooltip } from 'antd';
import { ChevronDown, ChevronUp, Information } from '../../assets/icons';
import FloatingLabel from '../../components/FloatingLabel/FloatingLabel';
import useShareCountValidation from '../../Hooks/useShareCountValidation';
import { getUsCurrencyFormat } from '../../utils/usCurrencyFormatter';
import { TOOLTIP_TEXT, TRANSACTION_VALIDATION_MESSAGES } from '../../utils/constant';

const BuyOrder = React.forwardRef((props, ref) => {
  const {
    numberOfShare,
    isMaxRangeError,
    sharesRemainingToPurchase,
    setNumberOfShare,
    limitPrice,
    setLimitPrice,
    LowestAskPrice,
    quickBuyPropertyData,
    totalInvestment,
    transactionFee,
    totalPurchase,
    rawTotalPurchase,
    partialFills,
    setPartialFills,
    sharesRemainingToPurchaseForSecondary,
    minimumPrice,
  } = props;
  const [expandAdvanced, setExpandAdvanced] = useState(true);
  const [isTextSelected, setIsTextSelected] = useState(false);
  const { isBelowMinShares, onShareInputChange } = useShareCountValidation(numberOfShare);
  const hasShareError = isBelowMinShares || (numberOfShare && (isMaxRangeError || Number(numberOfShare) % 1 !== 0));

  return (
    <div className='buy-content'>
      <Form ref={ref} form={ref?.current}>
        <FloatingLabel label='Number of shares' inputId='shares' inputValue={numberOfShare} alwaysBig>
          <Form.Item
            className='mb-12 mt-42'
            validateStatus={hasShareError && 'error'}
            help={
              hasShareError &&
              (Number(numberOfShare) % 1 !== 0
                ? 'Decimal numbers are not allowed.'
                : isBelowMinShares
                ? TRANSACTION_VALIDATION_MESSAGES.INVALID_SHARE_NUMBER_BUY
                : 'You have exceeded the Buy out limit.(i.e more than 9.8%).')
            }
          >
            <Input
              value={numberOfShare}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setNumberOfShare(value);
                onShareInputChange(value);
              }}
              onSelect={(e) => {
                window.getSelection().toString().length > 0 ? setIsTextSelected(true) : setIsTextSelected(false);
              }}
              onKeyPress={(event) => {
                if ((!/[0-9]/.test(event.key) || Number(event.target.value) >= Number(sharesRemainingToPurchaseForSecondary?.toFixed(0))) && !isTextSelected) {
                  event.preventDefault();
                }
              }}
              id='share'
              type='number'
              onWheel={(e) => {
                e.target.blur();
              }}
              controls={false}
            />
          </Form.Item>
        </FloatingLabel>
        <div className='mb-30 buy-tags'>
          {/* <Tag
          className={
            numberOfShare?.toString() === minButtonValue.toString() &&
            "ant-tag__selected"
          }
          onClick={() => setNumberOfShare(minButtonValue.toString())}
        >
          {`min: ${minButtonValue.toString()}`}
        </Tag> */}
          <Tag
            className={
              Number(sharesRemainingToPurchase.toFixed(0)) < 100 ? 'ant-tag-disabled' : numberOfShare?.toString() === '100' && 'ant-tag__selected'
            }
            onClick={() => Number(sharesRemainingToPurchase.toFixed(0)) >= 100 && setNumberOfShare('100')}
          >
            most popular: 100
          </Tag>
          <Tag
            className={
              !sharesRemainingToPurchaseForSecondary || Number(sharesRemainingToPurchaseForSecondary) <= 0
                ? 'ant-tag-disabled'
                : numberOfShare?.toString() === sharesRemainingToPurchaseForSecondary?.toFixed(0)?.toString() && 'ant-tag__selected'
            }
            onClick={() => {
              if (sharesRemainingToPurchaseForSecondary && Number(sharesRemainingToPurchaseForSecondary) > 0) {
                setNumberOfShare(sharesRemainingToPurchaseForSecondary?.toString() || '0');
              }
            }}
          >
            {`Max (9.8%): ${getUsCurrencyFormat(sharesRemainingToPurchaseForSecondary?.toString() || 0)}`}
          </Tag>
        </div>
        <FloatingLabel label='Limit price' inputId='shares' inputValue={limitPrice} alwaysBig>
          <Form.Item
            className='mb-12'
            validateStatus={
              (limitPrice && !/^(?!0\d)(\d+(?:\.\d{1,2})?|\.\d{1,2})$/.test(limitPrice)) ||
              (limitPrice && Number(limitPrice) < 0.01) ||
              (limitPrice && Number(limitPrice) > 10000) ||
              (minimumPrice && limitPrice && Number(limitPrice) < Number(minimumPrice))
                ? 'error'
                : ''
            }
            help={
              limitPrice && !/^(?!0\d)(\d+(?:\.\d{1,2})?|\.\d{1,2})$/.test(limitPrice)
                ? TRANSACTION_VALIDATION_MESSAGES.INVALID_PRICE_FORMAT
                : limitPrice && Number(limitPrice) < 0.01
                ? 'Please note that the minimum Limit Price is $0.01. Adjust the Limit Price to proceed.'
                : limitPrice && Number(limitPrice) > 10000
                ? 'Please note that the maximum Limit Price is $10,000. Adjust the Limit Price to proceed.'
                : minimumPrice && limitPrice && Number(limitPrice) < Number(minimumPrice)
                ? `The minimum share price for this property is $${Number(minimumPrice).toFixed(2)} per share. Please adjust your price to $${Number(minimumPrice).toFixed(2)} or more to place your buy order.`
                : ''
            }
          >
            <Input
              value={limitPrice}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                  setLimitPrice(val);
                }
              }}
              placeholder='$'
              onKeyPress={(event) => {
                const val = event.target.value;
                const dotIndex = val.indexOf('.');
                if (
                  !/[0-9.]/.test(event.key) ||
                  (event.key === '.' && val.includes('.')) ||
                  (dotIndex !== -1 && val.slice(dotIndex + 1).length >= 2)
                ) {
                  event.preventDefault();
                }
              }}
              id='limit'
            />
          </Form.Item>
        </FloatingLabel>
        <div className='mb-30 buy-tags'>
          <Tag
            className={
              Number(LowestAskPrice) <= 0 ? 'ant-tag-disabled' : limitPrice?.toString() === LowestAskPrice?.toString() && 'ant-tag__selected'
            }
            onClick={() => Number(LowestAskPrice) > 0 && setLimitPrice(LowestAskPrice?.toString() || '0')}
          >
            {`Best ask: $${getUsCurrencyFormat(LowestAskPrice || 0)} `}
          </Tag>
          <Tag
            className={limitPrice?.toString() === quickBuyPropertyData?.lastTradedPrice?.toString() && 'ant-tag__selected'}
            onClick={() => setLimitPrice(quickBuyPropertyData?.lastTradedPrice?.toString() || '0')}
          >
            {`Last price: $${getUsCurrencyFormat(quickBuyPropertyData?.lastTradedPrice || 0)}`}
          </Tag>
        </div>
        {/* Duration dropdown commented out - default value 'Good Until Cancel' is sent in payload */}
        {/* <FloatingLabel label='Duration' inputId='duration' inputValue={duration} alwaysBig>
          <>
            <Form.Item className='mb-8' name='duration'>
              <Select
                id='duration'
                popupClassName='filter-select-dropdown'
                onChange={(value) => setDuration(value)}
                value={duration}
                defaultValue={duration}
                onDropdownVisibleChange={() => setArrowUpDuration(!arrowUpDuration)}
                suffixIcon={arrowUpDuration ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
              >
                <Select.Option value='Good Until Cancel'>Good until cancel</Select.Option>
                <Select.Option value='Good For The Day'>Good for the day</Select.Option>
              </Select>
            </Form.Item>
          </>
        </FloatingLabel> */}
        {/* <div className='d-flex py-8 px-12 fz-12 bg-parchment radius-4 g-16 mb-24'>
            <div>
              <p>
                A "good until cancel" order is a buy or sell order that remains active until it is either executed or until the investor cancels it.
              </p>
              <p className='mb-0'>
                A "good for the day" order is a buy or sell order that is only good for the remainder of the trading day on which it is placed. All
                "good for the day" orders will expire at 11:59 PM EST on the day they were placed.
              </p>
            </div>
            <div className='no-shrink cursor-pointer' onClick={() => setDurationInfo(false)}>
              <CloseIcon size={16} color='#726460' />
            </div>
          </div> */}
        <div className='mb-0 basic-info'>
          <div className='mb-24 basic-info'>
            <div className='basic-info__item'>
              <span className='basic-info__item__name'>Total investment (USD)</span>
              <span className='basic-info__item__value fw-600'>{`$${getUsCurrencyFormat(totalInvestment || 0)}`}</span>
            </div>
            <div className='basic-info__item'>
              <span className='basic-info__item__name'>Platform fee</span>
              <span className='basic-info__item__value'>$2.50</span>
            </div>
            <div className='basic-info__item'>
              <span className='basic-info__item__name'>Broker-Dealer/ATS Fee (1%)</span>
              <span className='basic-info__item__value'> {`$${transactionFee || ''}`}</span>
            </div>
            <div className='basic-info__item'>
              <span className='basic-info__item__name'>Total purchase (USD)</span>
              <span className='basic-info__item__value fw-600'>{`$${totalPurchase || ''}`}</span>
            </div>
          </div>
          {numberOfShare && limitPrice && Number(rawTotalPurchase) > 0 && Number(rawTotalPurchase) < 10 && (
            <div className='ant-form-item-explain-error mb-12'>{TRANSACTION_VALIDATION_MESSAGES.MIN_AMOUNT}</div>
          )}
          {numberOfShare && limitPrice && Number(rawTotalPurchase) <= 0 && (
            <div className='ant-form-item-explain-error mb-12'>{TRANSACTION_VALIDATION_MESSAGES.MIN_AMOUNT}</div>
          )}
          {numberOfShare && limitPrice && Number(rawTotalPurchase) > 10000 && (
            <div className='ant-form-item-explain-error mb-12'>{TRANSACTION_VALIDATION_MESSAGES.MAX_AMOUNT}</div>
          )}
        </div>
        <div className='mb-40 fz-16'>
          <p className='fw-600 mb-8 cursor-pointer' onClick={() => setExpandAdvanced(!expandAdvanced)}>
            Advanced {expandAdvanced ? <ChevronUp verticalAlign='-7px' /> : <ChevronDown verticalAlign='-7px' />}
          </p>
          {!!expandAdvanced && (
            <div className='options'>
              <Checkbox checked={partialFills} onChange={(e) => setPartialFills(e.target.checked)} className='fz-16'>
                Accept partial fills
                <Tooltip
                  popupClassName='div-yeild-pop'
                  title={TOOLTIP_TEXT.PARTIAL_FILLS}
                  trigger='hover'
                >
                  <span>
                    <Information className='cursor-pointer' marginLeft='4px' verticalAlign='sub' color='#1A1519' />
                  </span>
                </Tooltip>
              </Checkbox>
            </div>
          )}
        </div>
      </Form>
    </div>
  );
});

export default BuyOrder;
